// ZOD
import * as z from 'zod';
import { OfferType, OfferZod, OfferSlugType, OfferSlugZod } from '@/views/Offer/OfferType';
import {
  JobApplicationFormValidationType,
  JobApplicationFormValidationZod,
} from '@/components/blocks/JobApplicationForm/JobApplicationFormType';

// Strapi
import { blocks, seo } from '@/services/strapiPopulate';
import { fetchStrapiContentWithBlocks } from '@/services/blocks/fetchStrapiContentWithBlocks';

// Utils
import { formatZodError } from '@/utils/error';

// Client
import client from '../client';

export const ApplicationDataZod = z.object({
  firstname: z.string(),
  lastname: z.string(),
  email: z.string(),
  phone: z.string().optional(),
  comment: z.string().optional(),
  files: z.array(z.instanceof(File)).optional(),
  type: z.string().optional(),
  dreamJob: z.string().optional(),
  marketingConsent: z.boolean(),
});

export type ApplicationDataType = z.infer<typeof ApplicationDataZod>;

export const PostOfferMutationZod = z.object({
  firstname: z.string().min(1, { message: 'Le prénom est requis' }),
  lastname: z.string().min(1, { message: 'Le nom est requis' }),
  email: z
    .string()
    .email({ message: 'L\'adresse email est invalide' })
    .min(1, { message: 'L\'adresse email est requise' }),
  phone: z.string().optional(),
  comment: z.string().optional(),
  marketingConsent: z.boolean().refine((val) => val === true, { message: 'Le consentement est requis' }),
  files: z.array(z.instanceof(File)).optional(),
});

export type PostOfferMutation = z.infer<typeof PostOfferMutationZod>;

/**
 * Get offer page data
 */
export const getOffer = async (slug: string): Promise<OfferType> =>
  fetchStrapiContentWithBlocks(
    `/api/offers/${slug}`,
    OfferZod,
    {
      title: {
        populate: '*',
      },
      paragraph: {
        populate: '*',
      },
      titleOverview: {
        populate: '*',
      },
      paragraphOverview: {
        populate: '*',
      },
      tags: {
        populate: '*',
      },
      blocks,
      seo,
    },
    'Offer',
  );

export const getOffers = async (): Promise<Array<OfferSlugType>> => {
  const pageSize = 10;
  const response = await client.get('/api/offers', {
    params: {
      fields: ['slug'],
      populate: {
        seo,
      },
      pagination: {
        page: 1,
        pageSize,
      },
    },
  });

  const totalPages = response.data?.meta?.pagination.pageCount || 1;
  const allData = [...(response.data?.data ?? [])];

  try {
    if (totalPages > 1) {
      const pagePromises = Array.from({ length: totalPages - 1 }, (_, i) =>
        client.get('/api/offers', {
          params: {
            fields: ['slug'],
            populate: {
              seo,
            },
            pagination: { page: i + 2, pageSize },
          },
        }),
      );
      const results = await Promise.all(pagePromises);
      results.forEach((r) => allData.push(...(r.data?.data || [])));
    }

    return z.array(OfferSlugZod).parseAsync(allData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error(`API response do not match Offers structure:\n${formatZodError(error, allData)}`);
    }
    throw error;
  }
};

/**
 * Post offer application data
 */
export const postOffer = async (data: PostOfferMutation): Promise<{ data: { documentId: string } }> => {
  try {
    const response = await client.post('/api/offer-applications', { data });
    return response?.data;
  } catch (error) {
    console.error('Error posting offer application:', error);
    throw error;
  }
};

export const postApplication = async (
  data: JobApplicationFormValidationType,
): Promise<{ data: { documentId: string } }> => {
  try {
    await JobApplicationFormValidationZod.parseAsync(data);
    const response = await client.post('/api/job-applications', { data });
    return response?.data;
  } catch (error) {
    if (error instanceof z.ZodError) {
      throw new Error(`Invalid job application data:\n${formatZodError(error, data)}`);
    }
    throw error;
  }
};

export const postSpontaneousApplication = async (
  data: JobApplicationFormValidationType,
): Promise<JobApplicationFormValidationType> => {
  const formData = new FormData();
  (Object.keys(data) as Array<keyof typeof data>).forEach((key) => {
    if (Array.isArray(data[key])) {
      (data[key] as File[]).forEach((file) => formData.append(key, file));
    } else if (data[key] !== undefined) {
      formData.append(key, String(data[key]));
    }
  });

  const response = await client.post('/api/form-job-application/custom-submit', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  try {
    return response.data;
  } catch (error) {
    console.error('Error fetching application config:', error);
    throw error;
  }
};
